import { MatAutocompleteModule } from '@angular/material/autocomplete';

import {
    APP_BASE_HREF,
    CommonModule,
    NgOptimizedImage,
    PlatformLocation,
} from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    APP_INITIALIZER,
    LOCALE_ID,
    NgModule,
    OnInit,
    isDevMode,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
    MAT_LUXON_DATE_ADAPTER_OPTIONS,
    provideLuxonDateAdapter,
} from '@angular/material-luxon-adapter';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CookieService } from 'ngx-cookie-service';
import {
    LottieComponent,
    LottieDirective,
    provideCacheableAnimationLoader,
    provideLottieOptions,
} from 'ngx-lottie';
import {
    NgxMaskDirective,
    NgxMaskPipe,
    provideEnvironmentNgxMask,
} from 'ngx-mask';
import { concat, lastValueFrom } from 'rxjs';
import { AlertModule } from 'src/lib/ariadna/alert';
import {
    APP_LOCALE,
    APP_MATERIAL_DATE_FORMATS,
} from './shared/date/date.utils';
import { AlertRenderDirective } from './alertRender.directive';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AccountComponent } from './components/account/account.component';
import { HeaderComponent } from './components/application/header/header.component';
import { MenuLeftComponent } from './components/application/menu-left/menu-left.component';
import { DicomViewModalComponent } from './components/history/dicom-view-modal/dicom-view-modal.component';
import { HistoryComponent } from './components/history/history.component';
import { ListCompletedComponent } from './components/history/list-completed/list-completed.component';
import { VisitCardComponent } from './components/history/list-completed/visit-card/visit-card.component';
import { ListUpcomingComponent } from './components/history/list-upcoming/list-upcoming.component';
import { HomeComponent } from './components/home/home.component';
import { IndexComponent } from './components/home/index/index.component';
import { FeedbackModalComponent } from './modals/feedback/feedback.component';
import { LabsPcComponent } from './components/labs/labs-pc/labs-pc.component';
import { OrderDetailsComponent } from './components/labs/labs-pc/order-details/order-details.component';
import { LabsComponent } from './components/labs/labs.component';
import { LoginFormComponent } from './components/auth/login-form/login-form.component';
import { SignDocumentsComponent } from './components/auth/sign-documents/sign-documents.component';
import { RecoveryComponent } from './components/auth/recovery/recovery.component';
import { RegistrationComponent } from './components/auth/registration/registration.component';
import { RecoveryEntryPassComponent } from './components/auth/recovery/entry-pass/entry-pass.component';
import { PatientFilesComponent } from './components/patient-files/patient-files.component';
import { PayPeriodDialogComponent } from './components/payments/dialogs/pay-period-dialog/pay-period-dialog.component';
import { PayDetailsDialogComponent } from './components/payments/dialogs/pays-details-dialog/pay-details-dialog.component';
import { IsRefundPipe } from './components/payments/is-refund.pipe';
import { PaymentsComponent } from './components/payments/payments.component';
import { CustomCalendarComponent } from './components/rec-services/custom-calendar/custom-calendar.component';
import { FilterComponent } from './components/rec-services/filter-component/filter-component';
import { RecServicesMobileComponent } from './components/rec-services/rec-services-mobile/rec-services-mobile.component';
import { RecServicesComponent } from './components/rec-services/rec-services.component';
import { DoctorCardComponent } from './components/rec/doctor-card/doctor-card.component';
import { recMobileDocsrvList } from './components/rec/rec-mobile/rec-mobile-docsrv-list/rec-mobile-docsrv-list';

// import { TalonsComponent } from './components/rec/rnumb-selected/talons/talons.component';
import { RecComponent } from './components/rec/rec.component';
import { ServicesDetailsDialogComponent } from './components/services/dialogs/services-details-dialog/services-details-dialog.component';
import { ServicesComponent } from './components/services/services.component';
import { BarcodeComponent } from './components/shared/barcode/barcode.component';
import { BlockTalonsComponent } from './components/shared/block-talons/block-talons.component';
import { CheckboxComponent } from './components/shared/checkbox/checkbox.component';
import { ChipListComponent } from './components/shared/chip-list/chip-list.component';
import { IsChipSelectedPipe } from './components/shared/chip-list/is-chip-selected.pipe';
import { ChipComponent } from './components/shared/chip/chip.component';
import { DateRangeComponent } from './components/shared/date-range/date-range.component';
import { MobileDateRangeComponent } from './components/shared/date-range/mobile-date-range/mobile-date-range.component';
import { TableDayComponent } from './components/shared/date-range/table-day/table-day.component';
import { LoaderComponent } from './components/shared/loader/loader.component';
import { FeedHeaderPeriodComponent } from './components/shared/mobile-feed-page-template/feed-header-period/feed-header-period.component';
import { FeedPageAnchorComponent } from './components/shared/mobile-feed-page-template/feed-page-anchor/feed-page-anchor.component';
import { MobileFeedPageTemplateComponent } from './components/shared/mobile-feed-page-template/mobile-feed-page-template.component';
import { PaginationComponent } from './components/shared/pagination/pagination.component';
import { PanelComponent } from './components/shared/panel/panel.component';
import { ProtocolControlComponent } from './components/shared/protocol-control/protocol-control.component';
import { ScrollViewComponent } from './components/shared/scroll-view/scroll-view.component';
import { SelectComponent } from './components/shared/select/select.component';
import { TextInputComponent } from './components/shared/text-input/text-input.component';
import { TextComponent } from './components/shared/text/text.component';
import { FilterTalonsByDayPipe } from './components/talonpicker/filter-by-day.pipe';
import { CalendarHeader } from './components/talonpicker/header/calendar-header.component';
import { TalonpickerComponent } from './components/talonpicker/talonpicker.component';
import { ProtocolFormComponent } from './components/visit-protocol/protocol-form/protocol-form.component';
import { VisitProtocolComponent } from './components/visit-protocol/visit-protocol.component';
import { datePipe } from './directives/date.pipe';
import { evenoddPipe } from './directives/evenodd.pipe';
import { isNotDatePipe } from './directives/isNotDate';
import { ObserveVisibilityDirective } from './directives/observe-visibility.directive';
import { phoneMaskDirective } from './directives/phoneMask.directive';
import { PriceSpacePipe } from './directives/price-space.pipe';
import { SafeHtmlPipe } from './directives/safe-html.pipe';
import { SearchFilterPipe } from './directives/searchFilter.pipe';
import { snilsMaskDirective } from './directives/snilsMask.directive';
import { SortByPipe } from './directives/sortBy.pipe';
import { getBaseHref } from './helpers/help';
import { ApiResponseInterceptor } from './interceptors/ApiResponseInterceptor';
import { ChangePwComponent } from './modals/change-pw/change-pw.component';
import { CreatePwComponent } from './modals/create-pw/create-pw.component';
import { DocImgComponent } from './modals/doc-img/doc-img.component';
import { DocInfoModalComponent } from './modals/doc-info-modal/doc-info-modal.component';
import { FamilyAccessComponent } from './modals/family-access/family-access.component';
import { HistoryItemComponentMobile } from './modals/history-item-mobile/history-item.component';
import { HistoryItemComponent } from './modals/history-item/history-item.component';
import { HistoryPdfModal } from './modals/history-pdf-modal/history-pdf.component';
import { NewTalonComponentMobile } from './modals/new-talon-mobile/talon.component';
import { RefundComponent } from './modals/refund/refund.component';
import { StatementComponent } from './modals/statement/statement.component';
import { ConfigService } from './services/application/config.service.';
import { HttpNewService } from './services/application/http-new.service';
import { LoadService } from './services/application/load.service';
import { AuthService } from './services/auth.service';
import { compToPagination } from './services/compToPagination';
import { CookieConsentService } from './services/cookie-consent/cookie-consent-service';
import { Size } from './services/size';
// import { RecDoctorService } from './services/to-doctor/rec-doctor.service';
import { I18n } from './shared/i18n/models';

import { SmsComponent } from './components/account/sms/sms.component';
import { SemdViewComponent } from './components/history/list-completed/semd-view.component/semd-view.component';
import { MobileAppointmentComponent } from './components/rec/mobile-appointment/mobile-appointment.component';
import { BonusCasePipe } from './directives/bonus-case.pipe';
import { HtmlContentPipe } from './directives/html-content.pipe';
import { PatInfoForPayModalComponent } from './modals/pat-info-for-pay/for-pay.component';
import { ReseachChildModalComponent } from './modals/reseach-child-modal/reseach-child-modal.component';
import { SemdModalComponent } from './modals/semd-modal/semd-modal.component';
import { PhoneMaskDirective } from './directives/phone.validator';
import { ProgressBarComponent } from './components/shared/progress-bar/progress-bar.component';
import { OtpInputComponent } from './components/shared/otp-input/otp-input.component';
import { PwaInstallButtonComponent } from './components/shared/pwa-install-button/pwa-install-button.component';
import { PwaInstallInstructionsDialogComponent } from './components/shared/pwa-install-button/pwa-install-instructions-dialog.component';
import { PwaUpdateDialogComponent } from './components/shared/pwa-update-dialog/pwa-update-dialog.component';
import { AuthCodesComponent } from './components/auth/codes/codes.component';
import { RegisterSuccessComponent } from './components/auth/registration/reg-success/reg-success.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { EmailInputModalComponent } from './modals/email-input/email-input.component';
export function playerFactory() {
    return import('lottie-web');
}

@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        HomeComponent,
        LoginFormComponent,
        SignDocumentsComponent,
        RecoveryComponent,
        RecoveryEntryPassComponent,
        AuthCodesComponent,
        RegistrationComponent,
        RegisterSuccessComponent,
        snilsMaskDirective,
        phoneMaskDirective,
        datePipe,
        isNotDatePipe,
        PriceSpacePipe,
        SortByPipe,
        evenoddPipe,
        MenuLeftComponent,
        AccountComponent,
        IndexComponent,
        PanelComponent,
        PaginationComponent,
        StatementComponent,
        ServicesComponent,
        PaymentsComponent,
        LabsComponent,
        RecComponent,
        HistoryComponent,
        HistoryItemComponent,
        DateRangeComponent,
        ChangePwComponent,
        TableDayComponent,
        HistoryItemComponentMobile,
        PatInfoForPayModalComponent,
        EmailInputModalComponent,
        ObserveVisibilityDirective,
        MobileFeedPageTemplateComponent,
        FeedPageAnchorComponent,
        MobileDateRangeComponent,
        ServicesDetailsDialogComponent,
        LabsPcComponent,
        OrderDetailsComponent,
        DocImgComponent,
        CreatePwComponent,
        PayPeriodDialogComponent,
        PayDetailsDialogComponent,
        NewTalonComponentMobile,
        MobileAppointmentComponent,
        ProtocolFormComponent,
        ProtocolControlComponent,
        VisitProtocolComponent,
        TextComponent,
        SelectComponent,
        ChipComponent,
        ChipListComponent,
        SafeHtmlPipe,
        HtmlContentPipe,
        LoaderComponent,
        ProgressBarComponent,
        IsChipSelectedPipe,
        RefundComponent,
        IsRefundPipe,
        CheckboxComponent,
        TextInputComponent,
        FeedHeaderPeriodComponent,
        ScrollViewComponent,
        ListUpcomingComponent,
        ListCompletedComponent,
        BarcodeComponent,
        DicomViewModalComponent,
        VisitCardComponent,
        SearchFilterPipe,
        DocInfoModalComponent,
        RecServicesComponent,
        HistoryPdfModal,
        FeedbackModalComponent,
        PatientFilesComponent,
        TalonpickerComponent,
        CalendarHeader,
        FilterTalonsByDayPipe,
        FamilyAccessComponent,
        FilterComponent,
        RecServicesMobileComponent,
        CustomCalendarComponent,
        SmsComponent,
        recMobileDocsrvList,
        ReseachChildModalComponent,
        DoctorCardComponent,
        SemdViewComponent,
        SemdModalComponent,
        BonusCasePipe,
        PhoneMaskDirective,
        OtpInputComponent,
        PwaInstallButtonComponent,
        PwaInstallInstructionsDialogComponent,
        PwaUpdateDialogComponent,
    ],
    bootstrap: [AppComponent],
    exports: [],
    imports: [
        NgOptimizedImage,
        LottieComponent,
        LottieDirective,
        AlertRenderDirective,
        BrowserModule,
        AppRoutingModule,
        NgbModule,
        NgbTooltipModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatMenuModule,
        MatDialogModule,
        MatTabsModule,
        MatSelectModule,
        MatListModule,
        MatExpansionModule,
        AlertModule,
        BrowserAnimationsModule,
        BlockTalonsComponent,
        NgxMaskDirective,
        NgxMaskPipe,
        CommonModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerImmediately',
        }),
    ],
    providers: [
        {
            provide: APP_BASE_HREF,
            useFactory: getBaseHref,
            deps: [PlatformLocation],
        },
        // { provide: JsonParser, useClass: CustomJsonParser },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiResponseInterceptor,
            multi: true,
        },
        provideLottieOptions({ player: playerFactory }),
        provideCacheableAnimationLoader(),
        CookieConsentService,
        HttpNewService,
        LoadService,
        ConfigService,
        AuthService,
        // RecDoctorService,
        Size,
        compToPagination,
        CookieService,
        I18n,
        { provide: LOCALE_ID, useValue: APP_LOCALE },
        { provide: MAT_DATE_LOCALE, useValue: APP_LOCALE },
        ...provideLuxonDateAdapter(APP_MATERIAL_DATE_FORMATS),
        {
            provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
            useValue: {
                useUtc: false,
                firstDayOfWeek: 1,
                defaultOutputCalendar: 'gregory',
            },
        },
        provideEnvironmentNgxMask(),
        {
            provide: APP_INITIALIZER,
            multi: true,
            deps: [ConfigService, LoadService],
            // ТУТ НАДО ПЕРЕДЕЛАТЬ, РАЗОБРАТЬСЯ ЧЕ ЗА 3 РЕТЕРНА И ЗАЧЕМ
            useFactory: (configS: ConfigService, loadS: LoadService) => {
                return async () => {
                    try {
                        await lastValueFrom(configS.loadConfig());
                        await lastValueFrom(
                            concat(loadS.loadAppConfig(), loadS.loadLpu()),
                        );
                    } catch (error) {
                        console.error(
                            'Ошибка при инициализации приложения',
                            error,
                        );
                    }
                };
            },
        },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule implements OnInit {
    public constructor(
        private iconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private cookieConsentService: CookieConsentService,
    ) {
        this.iconRegistry.addSvgIcon(
            'password-visible',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/auth/password-visible.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'password-hidden',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/auth/password-hidden.svg',
            ),
        );

        this.iconRegistry.addSvgIcon(
            'home',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/home/home.svg',
            ),
        );

        this.iconRegistry.addSvgIcon(
            'doctor-appointment-alternate',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/home/doctor-appointment-alternate.svg',
            ),
        );

        this.iconRegistry.addSvgIcon(
            'doctor-appointment',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/home/doctor-appointment.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'laboratory',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/home/laboratory.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'visit-history',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/home/visit-history.svg',
            ),
        );

        this.iconRegistry.addSvgIcon(
            'service-appointment',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/home/service-appointment.svg',
            ),
        );

        this.iconRegistry.addSvgIcon(
            'services',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/home/services.svg',
            ),
        );

        this.iconRegistry.addSvgIcon(
            'finances',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/home/finances.svg',
            ),
        );

        this.iconRegistry.addSvgIcon(
            'tax-refund',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/account/tax-refund.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'logout',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/account/logout.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'profile',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/account/profile.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'refresh',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/actions/refresh.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'contract',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/account/contract.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'login-information',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/account/login-information.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'close-dialog',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/actions/close-dialog.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'payment-card-mobile',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/payments/payment-card-mobile.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'payment-card',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/payments/payment-card.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'date-range',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/calendar/date-range.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'download',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/actions/download.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'location',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/navigation/location.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'print',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/actions/print.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'telemedicine',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/appointments/telemedicine.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'chevron-right',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/navigation/chevron-right.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'chevron-left',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/navigation/chevron-left.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'search',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/actions/search.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'check',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/status/check.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'navigation-back',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/navigation/back-secondary.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'refund-mobile',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/payments/refund-mobile.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'refund',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/payments/refund.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'calendar',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/calendar/calendar.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'credit-card',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/payments/credit-card.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'loading',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/actions/loading.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'remove',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/actions/remove.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'add',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/actions/add.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'feedback',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/account/feedback.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'documents',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/home/documents.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'family',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/account/family.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'doctor',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/appointments/doctor.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'facility',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/appointments/facility-filter.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'next-step',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/navigation/next-step.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'download-file',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/documents/download-file.svg',
            ),
        );
        this.iconRegistry.addSvgIcon(
            'no-preview',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/icons/documents/no-preview.svg',
            ),
        );
    }
    public ngOnInit(): void {
        void this.cookieConsentService.initialize();
    }
}
